/**
 * CoreFlow Authenticated API Helper
 * 
 * Wraps fetch() to automatically attach the Supabase JWT token
 * to all API requests. This ensures the server can verify the caller's
 * identity and org membership.
 */
import supabase from './supabase';

// ─── Refresh lock + retry helper ───
// Concurrent API requests that all hit 401 around the same time used to
// each fire their own refreshSession() in parallel. One would succeed and
// rotate the refresh token; the rest would race with the rotation and
// trip Supabase's "Detect and revoke compromised refresh tokens" guard
// (10s reuse interval) — Supabase revoked the entire chain and the next
// refresh failed definitively, force-signing out an active user.
//
// _refreshPromise is the shared in-flight refresh. All concurrent 401s
// await the same Promise instead of stampeding. retryWithBackoff adds
// resilience against transient network failures (3 attempts at 0/500ms/2s)
// before giving up — only then do we sign the user out.
let _refreshPromise = null;

async function refreshWithRetry(maxAttempts = 3) {
  // Share a single in-flight refresh across all concurrent callers.
  if (_refreshPromise) {
    console.log("[CoreFlow API] Refresh already in flight, awaiting");
    return _refreshPromise;
  }

  _refreshPromise = (async () => {
    const delays = [0, 500, 2000]; // immediate, 500ms, 2s
    let lastError = null;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      if (delays[attempt] > 0) {
        await new Promise(r => setTimeout(r, delays[attempt]));
      }
      try {
        const { data, error } = await supabase.auth.refreshSession();
        if (error) {
          lastError = error;
          console.warn("[CoreFlow API] Refresh attempt " + (attempt + 1) + " failed: " + error.message);
          continue;
        }
        if (data?.session?.access_token) {
          console.log("[CoreFlow API] Refresh succeeded on attempt " + (attempt + 1));
          return data.session;
        }
      } catch (e) {
        lastError = e;
        console.warn("[CoreFlow API] Refresh attempt " + (attempt + 1) + " threw: " + (e?.message || e));
      }
    }

    console.error("[CoreFlow API] All refresh attempts failed:", lastError?.message);
    return null;
  })();

  try {
    return await _refreshPromise;
  } finally {
    _refreshPromise = null;
  }
}

/**
 * Make an authenticated API call.
 * Automatically attaches the Supabase access_token as a Bearer token.
 * Handles 401 responses by redirecting to login.
 * 
 * @param {string} url - The API endpoint (e.g., "/api/sms/send")
 * @param {object} options - Standard fetch options (method, body, headers, etc.)
 * @returns {Promise<Response>} - The fetch response
 */
export async function apiFetch(url, options = {}) {
  // Get the current session token
  let token = null;
  try {
    const { data: { session } } = await supabase.auth.getSession();
    token = session?.access_token || null;
  } catch (e) {
    console.warn("[CoreFlow] Failed to get auth session for API call:", e.message);
  }

  // Merge headers: caller headers first, then auth on top (never overwritten)
  const headers = {
    "Content-Type": "application/json",
    ...(options.headers || {}),
  };

  // Always set auth header if we have a token (can't be overridden by caller)
  if (token) {
    headers["Authorization"] = "Bearer " + token;
  }

  const resp = await fetch(url, {
    ...options,
    headers,
  });

  // If server says unauthorized, the session is likely expired.
  // refreshWithRetry handles the concurrent-request stampede + transient
  // failures. signOut() only fires after all retries are exhausted, so
  // genuinely revoked sessions (signed-out elsewhere, password changed)
  // still produce the expected logout — just with more recovery chances
  // for transient cases.
  if (resp.status === 401) {
    console.warn("[CoreFlow API] 401 received, attempting session refresh");
    const refreshed = await refreshWithRetry(3);

    if (refreshed?.access_token) {
      console.log("[CoreFlow API] Retrying request with refreshed token");
      headers["Authorization"] = "Bearer " + refreshed.access_token;
      return fetch(url, { ...options, headers });
    }

    // Refresh definitively failed across all retries — session is genuinely dead.
    console.warn("[CoreFlow API] Refresh exhausted, signing out");
    await supabase.auth.signOut();
    window.history.pushState({}, "", "/login");
  }

  return resp;
}

/**
 * Make an authenticated API call and parse JSON response safely.
 * Returns { ok, status, data } where data is the parsed JSON or error info.
 */
export async function apiJson(url, options = {}) {
  try {
    const resp = await apiFetch(url, options);
    const text = await resp.text();
    let data;
    try {
      data = JSON.parse(text);
    } catch {
      data = { error: `Server error (${resp.status})`, _raw: text.slice(0, 200) };
    }
    return { ok: resp.ok, status: resp.status, data };
  } catch (err) {
    return { ok: false, status: 0, data: { error: err.message } };
  }
}

export default apiFetch;

